import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  BookOpen, 
  Camera, 
  Edit3, 
  Save, 
  X, 
  Lock,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    mobile: user?.mobile || '',
    targetYear: user?.targetYear || new Date().getFullYear(),
    preferredSubjects: user?.preferredSubjects || [],
    parentEmail: user?.parentEmail || '',
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, and GIF images are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await userAPI.uploadProfileImage(formData);
      if (response.data.success) {
        updateUser({ ...user, profileImage: response.data.data.profileImage });
        toast.success('Profile image updated successfully!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await userAPI.updateProfile(profileForm);
      if (response.data.success) {
        updateUser(response.data.data);
        toast.success('Profile updated successfully!');
        setIsEditing(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await userAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      if (response.data.success) {
        toast.success('Password changed successfully!');
        setIsChangingPassword(false);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSubjectToggle = (subject: string) => {
    setProfileForm(prev => ({
      ...prev,
      preferredSubjects: prev.preferredSubjects.includes(subject)
        ? prev.preferredSubjects.filter((s: string) => s !== subject)
        : [...prev.preferredSubjects, subject]
    }));
  };

  const subjects = [
    { id: 'mathematics', name: 'Mathematics', color: 'primary' },
    { id: 'physics', name: 'Physics', color: 'success' },
    { id: 'chemistry', name: 'Chemistry', color: 'warning' }
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Profile Settings</h1>
        <p className="text-secondary-600">Manage your account information and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Picture Section */}
        <div className="lg:col-span-1">
          <div className="card text-center">
            <div className="card-content">
              <div className="relative inline-block mb-4">
                {user?.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt={user.name}
                    className="w-32 h-32 rounded-full object-cover mx-auto"
                  />
                ) : (
                  <div className="w-32 h-32 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto">
                    <User className="h-16 w-16 text-white" />
                  </div>
                )}
                <label htmlFor="profile-image-upload" className="absolute bottom-2 right-2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-secondary-50 transition-colors cursor-pointer">
                  {imageUploading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-500 border-t-transparent"></div>
                  ) : (
                    <Camera className="h-5 w-5 text-secondary-600" />
                  )}
                </label>
                <input
                  id="profile-image-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={imageUploading}
                />
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-1">{user?.name}</h3>
              <p className="text-secondary-500 mb-4">{user?.email}</p>
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700">
                <BookOpen className="h-4 w-4 mr-1" />
                {user?.role === 'student' ? 'Student' : 'Admin'}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="card mt-6">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-secondary-900">Quick Stats</h3>
            </div>
            <div className="card-content">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-secondary-600">Tests Completed</span>
                  <span className="font-semibold text-secondary-900">12</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-secondary-600">Average Score</span>
                  <span className="font-semibold text-secondary-900">78%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-secondary-600">Study Streak</span>
                  <span className="font-semibold text-secondary-900">7 days</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-secondary-900">Personal Information</h3>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn-secondary btn-sm"
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setProfileForm({
                          name: user?.name || '',
                          mobile: user?.mobile || '',
                          targetYear: user?.targetYear || new Date().getFullYear(),
                          preferredSubjects: user?.preferredSubjects || [],
                          parentEmail: user?.parentEmail || '',
                        });
                      }}
                      className="btn-ghost btn-sm"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </button>
                    <button
                      onClick={handleProfileSubmit}
                      disabled={loading}
                      className="btn-primary btn-sm"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="card-content">
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      <User className="h-4 w-4 inline mr-2" />
                      Full Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                        className="input-field"
                        required
                      />
                    ) : (
                      <p className="text-secondary-900 py-2">{user?.name}</p>
                    )}
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      <Phone className="h-4 w-4 inline mr-2" />
                      Mobile Number
                    </label>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={profileForm.mobile}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, mobile: e.target.value }))}
                        className="input-field"
                        pattern="[6-9]\d{9}"
                        required
                      />
                    ) : (
                      <p className="text-secondary-900 py-2">{user?.mobile}</p>
                    )}
                  </div>

                  {/* Email (Read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      <Mail className="h-4 w-4 inline mr-2" />
                      Email Address
                    </label>
                    <p className="text-secondary-500 py-2">{user?.email} (Cannot be changed)</p>
                  </div>

                  {/* Target Year */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      <Calendar className="h-4 w-4 inline mr-2" />
                      Target Year
                    </label>
                    {isEditing ? (
                      <select
                        value={profileForm.targetYear}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, targetYear: parseInt(e.target.value) }))}
                        className="input-field"
                        required
                      >
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-secondary-900 py-2">{user?.targetYear}</p>
                    )}
                  </div>

                  {/* Parent Email */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      <Mail className="h-4 w-4 inline mr-2" />
                      Parent Email (Optional)
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={profileForm.parentEmail}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, parentEmail: e.target.value }))}
                        className="input-field"
                        placeholder="parent@example.com"
                      />
                    ) : (
                      <p className="text-secondary-900 py-2">{user?.parentEmail || 'Not provided'}</p>
                    )}
                  </div>
                </div>

                {/* Preferred Subjects */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-3">
                    <BookOpen className="h-4 w-4 inline mr-2" />
                    Preferred Subjects
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {subjects.map(subject => (
                      <button
                        key={subject.id}
                        type="button"
                        onClick={() => isEditing && handleSubjectToggle(subject.id)}
                        disabled={!isEditing}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          profileForm.preferredSubjects.includes(subject.id)
                            ? `bg-${subject.color}-100 text-${subject.color}-700 border-2 border-${subject.color}-300`
                            : 'bg-secondary-100 text-secondary-600 border-2 border-transparent hover:border-secondary-300'
                        } ${isEditing ? 'cursor-pointer' : 'cursor-default'}`}
                      >
                        {subject.name}
                      </button>
                    ))}
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Security Settings */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-secondary-900">Security Settings</h3>
                {!isChangingPassword ? (
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="btn-secondary btn-sm"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setIsChangingPassword(false);
                      setPasswordForm({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                      });
                    }}
                    className="btn-ghost btn-sm"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                )}
              </div>
            </div>
            <div className="card-content">
              {!isChangingPassword ? (
                <div className="flex items-center space-x-4 p-4 bg-secondary-50 rounded-xl">
                  <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center">
                    <Shield className="h-6 w-6 text-success-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-900">Password Protected</h4>
                    <p className="text-sm text-secondary-600">Your account is secured with a strong password</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  {/* Current Password */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className="input-field pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                      >
                        {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="input-field pr-12"
                        minLength={8}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                      >
                        {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="input-field pr-12"
                        minLength={8}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={passwordLoading}
                      className="btn-primary"
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      {passwordLoading ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}